import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Button } from './common/Button';
import { Link } from 'react-router-dom';

const SelectorContainer = styled.div`
  margin-top: ${theme.spacing['3xl']};
  padding: ${theme.spacing['2xl']};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
`;

const SelectorTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
  color: ${theme.colors.text.primary};
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ModeCard = styled.div<{ $active?: boolean; $locked?: boolean }>`
  background: ${props => props.$active ? theme.colors.primaryLight : 'white'};
  border: 2px solid ${props => props.$active ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.xl};
  cursor: ${props => props.$locked ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$locked ? 0.7 : 1};
  transition: all ${theme.transitions.base};
  position: relative;
  
  ${props => !props.$locked && !props.$active && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      border-color: ${theme.colors.primary};
    }
  `}
`;

const ModeIcon = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.spacing.md};
`;

const ModeTitle = styled.h4`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
`;

const ModeDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
  margin-bottom: ${theme.spacing.md};
`;

const ModeBadge = styled.span<{ $type: 'active' | 'locked' | 'upgrade' }>`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
  background: ${props => 
    props.$type === 'active' ? theme.colors.primary :
    props.$type === 'locked' ? '#94a3b8' :
    '#f59e0b'
  };
  color: white;
`;

interface Props {
  currentMode: 'prompts' | 'responses' | 'multi-llm';
  onModeChange: (mode: 'prompts' | 'responses' | 'multi-llm') => void;
  canUseResponseComparison: boolean;
  canUseMultiLLM: boolean;
  showResults?: boolean;
}

export const ComparisonModeSelector: React.FC<Props> = ({
  currentMode,
  onModeChange,
  canUseResponseComparison,
  canUseMultiLLM,
  showResults = false
}) => {
  const handleModeClick = (mode: 'prompts' | 'responses' | 'multi-llm') => {
    if (mode === 'responses' && !canUseResponseComparison) {
      return;
    }
    if (mode === 'multi-llm' && !canUseMultiLLM) {
      return;
    }
    onModeChange(mode);
  };

  return (
    <SelectorContainer>
      <SelectorTitle>
        {showResults ? '🎯 Want to see more?' : '📊 Choose Comparison Mode'}
      </SelectorTitle>
      
      <ModeGrid>
        <ModeCard 
          $active={currentMode === 'prompts'}
          onClick={() => handleModeClick('prompts')}
        >
          {currentMode === 'prompts' && <ModeBadge $type="active">Active</ModeBadge>}
          <ModeIcon>⚖️</ModeIcon>
          <ModeTitle>Method Comparison</ModeTitle>
          <ModeDescription>
            Compare how different improvement methods transform your prompt: 
            Original vs Promptasaurus vs Fossil Fills side-by-side.
          </ModeDescription>
          <Button 
            variant={currentMode === 'prompts' ? 'primary' : 'secondary'}
            size="small"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleModeClick('prompts');
            }}
          >
            {currentMode === 'prompts' ? 'Currently Viewing' : 'Compare Methods'}
          </Button>
        </ModeCard>

        <ModeCard 
          $active={currentMode === 'responses'}
          $locked={!canUseResponseComparison}
          onClick={() => handleModeClick('responses')}
        >
          {currentMode === 'responses' && <ModeBadge $type="active">Active</ModeBadge>}
          {!canUseResponseComparison && <ModeBadge $type="locked">Sign In Required</ModeBadge>}
          <ModeIcon>{canUseResponseComparison ? '✨' : '🔒'}</ModeIcon>
          <ModeTitle>Response Comparison</ModeTitle>
          <ModeDescription>
            See the actual AI responses generated by each prompt version. 
            Witness the real improvement in output quality.
          </ModeDescription>
          {canUseResponseComparison ? (
            <Button 
              variant={currentMode === 'responses' ? 'primary' : 'secondary'}
              size="small"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleModeClick('responses');
              }}
            >
              {currentMode === 'responses' ? 'Currently Viewing' : 'Show AI Responses'}
            </Button>
          ) : (
            <Button 
              as={Link}
              to="/login"
              variant="secondary"
              size="small"
              fullWidth
              onClick={(e) => e.stopPropagation()}
            >
              Sign In to Unlock
            </Button>
          )}
        </ModeCard>

        <ModeCard 
          $active={currentMode === 'multi-llm'}
          $locked={!canUseMultiLLM}
          onClick={() => handleModeClick('multi-llm')}
        >
          {currentMode === 'multi-llm' && <ModeBadge $type="active">Active</ModeBadge>}
          {!canUseMultiLLM && <ModeBadge $type="upgrade">Raptor/Rex Only</ModeBadge>}
          <ModeIcon>{canUseMultiLLM ? '⚡' : '🔒'}</ModeIcon>
          <ModeTitle>Multi-LLM Comparison</ModeTitle>
          <ModeDescription>
            Compare responses from both OpenAI and Anthropic side-by-side. 
            Find which AI performs best for your use case.
          </ModeDescription>
          {canUseMultiLLM ? (
            <Button 
              variant={currentMode === 'multi-llm' ? 'primary' : 'secondary'}
              size="small"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleModeClick('multi-llm');
              }}
            >
              {currentMode === 'multi-llm' ? 'Currently Viewing' : 'Compare LLMs'}
            </Button>
          ) : (
            <Button 
              as={Link}
              to="/pricing"
              variant="secondary"
              size="small"
              fullWidth
              onClick={(e) => e.stopPropagation()}
            >
              Upgrade to Unlock
            </Button>
          )}
        </ModeCard>
      </ModeGrid>
    </SelectorContainer>
  );
};